// Setter CRM depth (spec §08.1 CRM side, §08.2, §08.7, §08.8): the CRM side of one opportunity as shown in the lead view.
// Everything here reflects what the CRM actually confirmed — a deal is «vinculado» only after the CRM returned its id,
// a next action is «sincronizada» only after the write succeeded. Provider behavior is unverified (fake-tested).
import { z } from 'zod';
import { CrmProviderSchema } from './api-crm.js';
import { ConnectionStatusSchema } from './enums.js';

const Iso = z.string();

export const CRM_DEAL_STATES = ['none', 'linked', 'ambiguous', 'failed', 'unsupported', 'disabled'] as const;
export const CrmDealStateSchema = z.enum(CRM_DEAL_STATES);
export type CrmDealState = z.infer<typeof CrmDealStateSchema>;
export const CRM_DEAL_STATE_LABELS: Record<CrmDealState, string> = {
  none: 'Aún sin negocio en el CRM',
  linked: 'Negocio vinculado',
  ambiguous: 'Coincidencia ambigua: requiere revisión',
  failed: 'No se pudo crear o vincular',
  unsupported: 'No soportado por esta conexión',
  disabled: 'Negocios por proyecto desactivados en el mapeo',
};

export const CRM_READ_STATES = ['ok', 'error', 'unsupported', 'never'] as const;
export const CrmReadStateSchema = z.enum(CRM_READ_STATES);
export type CrmReadState = z.infer<typeof CrmReadStateSchema>;
export const CRM_READ_STATE_LABELS: Record<CrmReadState, string> = {
  ok: 'Estado del CRM verificado',
  error: 'CRM no disponible: se operó con el estado local',
  unsupported: 'Lectura en vivo no disponible',
  never: 'Aún no se ha consultado',
};

/** The opportunity's concrete next action (§08.2 principio operativo), computed from LIA's own schedule. */
export const NextActionSchema = z.object({
  label: z.string().nullable(),
  at: Iso.nullable(),
  /** whatsapp | voz | visita | humano | null */
  channel: z.string().nullable(),
  ownerName: z.string().nullable(),
  /** An active opportunity under LIA's responsibility with nothing scheduled (needs attention). */
  missing: z.boolean(),
});
export type NextAction = z.infer<typeof NextActionSchema>;

export const LeadCrmConnectionSyncSchema = z.object({
  connectionId: z.string(),
  provider: CrmProviderSchema,
  label: z.string(),
  status: ConnectionStatusSchema,
  deal: z.object({
    state: CrmDealStateSchema,
    externalId: z.string().nullable(),
    /** Deep link built from the documented URL pattern (sin verificar); null when the provider has none we trust. */
    url: z.string().nullable(),
    detail: z.string().nullable(),
    syncedAt: Iso.nullable(),
  }),
  lastRead: z.object({ state: CrmReadStateSchema, at: Iso.nullable(), detail: z.string().nullable() }),
  nextActionSynced: z.object({ label: z.string().nullable(), at: Iso.nullable(), syncedAt: Iso }).nullable(),
  lastExchangeNoteAt: Iso.nullable(),
  /** CRM writes of this contact queued (CRM down or retrying): kept locally, never shown as written. */
  pendingWrites: z.number().int(),
  /** Writes that stopped after retries (see «Errores de sincronización»). */
  parkedWrites: z.number().int(),
});
export type LeadCrmConnectionSync = z.infer<typeof LeadCrmConnectionSyncSchema>;

export const LeadCrmSyncSchema = z.object({
  interestId: z.string(),
  nextAction: NextActionSchema,
  connections: z.array(LeadCrmConnectionSyncSchema),
});
export type LeadCrmSync = z.infer<typeof LeadCrmSyncSchema>;
